<template>
    <!-- the match center: one title for both strips, the results on top and the next matches right below -->
    <div class="match-center">
        <div class="match-center__label">
            <span v-for="(row, index) in rows" :key="row.key" class="match-center__part">
                <span v-if="index" class="match-center__sep" />
                <v-icon small class="match-center__dir">{{ row.key === 'results' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}</v-icon>
                <span class="match-center__title">{{ row.title }}</span>
                <span v-if="row.matches.length" class="match-center__count">{{ row.matches.length }}</span>
                <span v-if="row.key === 'upcoming' && liveCount" class="match-center__live">
                    <span class="match-center__live-dot" />{{ liveCount }} LIVE
                </span>
            </span>
            <v-spacer />
            <router-link v-if="allTo" :to="allTo" class="match-center__all">
                {{ allLabel }}<v-icon x-small>mdi-arrow-right</v-icon>
            </router-link>
            <button v-else type="button" class="match-center__all" @click="$emit('show-all')">
                {{ allLabel }}<v-icon x-small>mdi-arrow-right</v-icon>
            </button>
        </div>

        <div v-for="row in rows" :key="row.key" class="match-center__row">
            <div v-if="!row.matches.length" class="cl-empty match-center__empty">{{ row.empty }}</div>
            <div v-else class="match-center__viewport">
                <button
                    v-show="hasMouse && !$vuetify.breakpoint.xs && canScroll[row.key] && canScroll[row.key].left"
                    type="button"
                    class="match-center__arrow match-center__arrow--left"
                    aria-label="Προηγούμενοι"
                    @click="scroll(row.key, -1)"
                >
                    <v-icon>mdi-chevron-left</v-icon>
                </button>
                <div :ref="`strip-${row.key}`" class="match-center__strip" @scroll.passive="updateScroll(row.key)">
                    <div
                        v-for="match in row.matches"
                        :key="match.id"
                        class="match-center__item"
                        :class="row.key === 'results' ? 'match-center__item--result' : 'match-center__item--upcoming'"
                    >
                        <result-card v-if="row.key === 'results'" :match="match" />
                        <match-card v-else :match="match" />
                    </div>
                </div>
                <button
                    v-show="hasMouse && !$vuetify.breakpoint.xs && canScroll[row.key] && canScroll[row.key].right"
                    type="button"
                    class="match-center__arrow match-center__arrow--right"
                    aria-label="Επόμενοι"
                    @click="scroll(row.key, 1)"
                >
                    <v-icon>mdi-chevron-right</v-icon>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import MatchCard from '@/components/MatchCard'
    import ResultCard from '@/components/ResultCard'
    import { isMatchLive, refreshLiveStatuses, withLiveData, pollWhileVisible, LIVE_TICK_MS } from '@/utils/matchLive'

    function time(match) {
        return match.matchDatetime ? momentTimezone(match.matchDatetime).valueOf() : 0
    }

    export default {
        components: { MatchCard, ResultCard },
        props: {
            matches: {
                type: Array,
                default: () => []
            },
            numOfResults: {
                type: Number,
                default: 20
            },
            // where "all the matches" leads; null: the parent handles the show-all event (a tab of the page)
            allTo: {
                type: Object,
                default: () => ({ name: 'Schedule' })
            },
            allLabel: {
                type: String,
                default: 'Όλο το πρόγραμμα'
            }
        },
        data() {
            return {
                // a mouse: arrows; touch screens and phone widths swipe
                hasMouse: typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)').matches : true,
                now: Date.now(),
                stopTick: null,
                // { upcoming: { left, right }, results: { left, right } }
                canScroll: {}
            }
        },
        computed: {
            // live score and end of game from the stat logs, ahead of the next reload of the list
            liveMatches() {
                return (this.matches || []).map(match => withLiveData(match, this.now))
            },
            upcoming() {
                return this.liveMatches
                    .filter(match => match.pending || this.isLive(match))
                    // live first, postponed last, the rest by date
                    .sort((a, b) => (this.isLive(b) - this.isLive(a)) || (Boolean(a.postponed) - Boolean(b.postponed)) || (time(a) - time(b)))
            },
            results() {
                return this.liveMatches
                    .filter(match => !match.pending && !match.postponed && !this.isLive(match))
                    .sort((a, b) => time(b) - time(a))
                    .slice(0, this.numOfResults)
            },
            liveCount() {
                return this.upcoming.filter(match => this.isLive(match)).length
            },
            rows() {
                // the results on top: the arrows of the title point to them (up) and to the next matches (down)
                return [
                    { key: 'results', title: 'Αποτελέσματα', matches: this.results, empty: 'Δεν υπάρχουν αποτελέσματα αυτή τη στιγμή.' },
                    { key: 'upcoming', title: 'Επόμενοι αγώνες', matches: this.upcoming, empty: 'Δεν υπάρχουν προγραμματισμένοι αγώνες αυτή τη στιγμή.' }
                ]
            }
        },
        watch: {
            matches: {
                handler() {
                    refreshLiveStatuses(this.matches || [])
                    this.$nextTick(this.updateAllScroll)
                },
                immediate: true
            }
        },
        created() {
            this.stopTick = pollWhileVisible(() => {
                this.now = Date.now()
                refreshLiveStatuses(this.matches || [], this.now)
            }, LIVE_TICK_MS)
        },
        mounted() {
            window.addEventListener('resize', this.updateAllScroll, { passive: true })
            this.$nextTick(this.updateAllScroll)
        },
        beforeDestroy() {
            this.stopTick()
            window.removeEventListener('resize', this.updateAllScroll)
        },
        methods: {
            isLive(match) {
                return isMatchLive(match, this.now)
            },
            stripEl(key) {
                const ref = this.$refs[`strip-${key}`]
                return Array.isArray(ref) ? ref[0] : ref
            },
            updateScroll(key) {
                const el = this.stripEl(key)
                const state = el
                    ? { left: el.scrollLeft > 4, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4 }
                    : { left: false, right: false }
                this.$set(this.canScroll, key, state)
            },
            updateAllScroll() {
                this.rows.forEach(row => this.updateScroll(row.key))
            },
            scroll(key, direction) {
                const el = this.stripEl(key)
                if (el) {
                    el.scrollBy({ left: direction * Math.max(el.clientWidth * 0.8, 240), behavior: 'smooth' })
                }
            }
        }
    }
</script>

<style scoped>
/* the two strips close together */
.match-center__row + .match-center__row {
    margin-top: 2px;
}

.match-center__label {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    margin-bottom: 8px;
}

.match-center__part {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.match-center__sep {
    width: 1px;
    height: 20px;
    margin-right: 4px;
    background: rgba(35, 35, 35, 0.2);
}

.match-center__dir {
    color: #009688 !important;
}

.match-center__title {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.35rem;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #232323;
}

.match-center__count {
    padding: 1px 8px;
    border-radius: 999px;
    background: rgba(35, 35, 35, 0.08);
    color: rgba(35, 35, 35, 0.7);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 700;
}

.match-center__live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    background: #ef4444;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.1em;
}

.match-center__live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #fff;
    animation: match-center-live 1.2s ease-in-out infinite;
}

@keyframes match-center-live {
    50% {
        opacity: 0.25;
    }
}

.match-center__all {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #009688 !important;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.82rem;
    font-weight: 700;
    text-decoration: none;
}

.match-center__all .v-icon {
    color: inherit !important;
}

.match-center__all:hover {
    text-decoration: underline;
}

.match-center__viewport {
    position: relative;
}

.match-center__strip {
    display: flex;
    gap: 14px;
    padding: 4px 2px 8px;
    overflow-x: auto;
    scroll-snap-type: x proximity;

    /* no scroll bar: the arrows (desktop) and a swipe (phones) move the strip */
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.match-center__strip::-webkit-scrollbar {
    display: none;
}

.match-center__item {
    flex: 0 0 auto;
    scroll-snap-align: start;
}

.match-center__item--upcoming {
    width: 340px;
}

.match-center__item--result {
    width: 230px;
}

.match-center__arrow {
    position: absolute;
    top: 50%;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    margin-top: -24px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 4px 14px rgba(35, 35, 35, 0.25);
}

.match-center__arrow:hover {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
}

.match-center__arrow:hover .v-icon {
    color: #fff !important;
}

.match-center__arrow--left {
    left: -12px;
}

.match-center__arrow--right {
    right: -12px;
}

@media (max-width: 700px) {
    .match-center__item--upcoming {
        width: 82vw;
    }

    .match-center__item--result {
        width: 200px;
    }

    .match-center__title {
        font-size: 1.05rem;
    }

    /* both titles on one line of a phone, the link below them */
    .match-center__part {
        gap: 5px;
    }

    .match-center__sep {
        margin-right: 0;
    }

    .match-center__all {
        flex-basis: 100%;
    }
}
</style>
